/**
 * J1 van blok J (Frank, 18 oktober 2026): een antwoord als document.
 *
 * Onder een antwoord staan zijn bijlagen als kaartje (`BijlageKaart`, dezelfde
 * als onder een vraag) en een knop Maak document met de formaten waar de backend
 * een schrijver voor heeft. Kiezen zet het bestand als bijlage op het antwoord
 * (`POST .../messages/{id}/document`); het kaartje verschijnt meteen en komt bij
 * het volgende laden van het gesprek gewoon terug, ook op de telefoon.
 *
 * ZONDER SCHRIJVER GEEN KNOP. De formaten komen van de backend, eenmaal per
 * sessie opgehaald en niet per bericht.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { FilePlus2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import BijlageKaart from "./BijlageKaart";
import HerschrijfVoorstel from "./HerschrijfVoorstel";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

let formatenBelofte = null;

/** De formaten van de backend, eenmaal per sessie. Een fout geeft een lege lijst. */
export function laadFormaten() {
  if (!formatenBelofte) {
    formatenBelofte = Promise.resolve(axios.get(`${API}/conversations/documenten/formaten`))
      .then((r) => (Array.isArray(r?.data?.formaten) ? r.data.formaten : []))
      .catch(() => { formatenBelofte = null; return []; });
  }
  return formatenBelofte;
}

/** Alleen voor de toetsen: de bewaarde formaten vergeten. */
export function vergeetFormaten() {
  formatenBelofte = null;
}

/** Een NIEUWE lijst met dit document erbij; een eerder document van hetzelfde
 *  formaat valt weg, een eigen bijlage blijft. Spiegel van `documenten.voeg_toe`. PUUR. */
export function metDocument(lijst, bijlage) {
  const rest = (lijst || []).filter((b) => !(b?.bron === "document" && b?.formaat === bijlage?.formaat));
  return [...rest, bijlage];
}

/** De melding bij een mislukt verzoek. PUUR. */
export function foutTekst(status, t) {
  if (status === 413) return t("Het document is groter dan 5 MB.");
  if (status === 400) return t("Van dit antwoord kan geen document worden gemaakt.");
  return t("Het document maken is niet gelukt.");
}

export default function AntwoordDocument({ message }) {
  const { t } = useTaal();
  const [bijlagen, setBijlagen] = useState(message.attachments || []);
  const [formaten, setFormaten] = useState([]);
  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState("");
  const [fout, setFout] = useState("");

  useEffect(() => { setBijlagen(message.attachments || []); }, [message.attachments]);

  useEffect(() => {
    let weg = false;
    // Een lege lijst verandert niets aan het scherm en zet dus ook niets.
    laadFormaten().then((f) => { if (!weg && f.length) setFormaten(f); });
    return () => { weg = true; };
  }, []);

  const maak = async (formaat) => {
    setBezig(formaat);
    setFout("");
    try {
      const r = await axios.post(
        `${API}/conversations/${message.conversation_id}/messages/${message.id}/document`, { formaat });
      setBijlagen((oud) => metDocument(oud, r.data.attachment));
      setOpen(false);
    } catch (e) {
      setFout(foutTekst(e?.response?.status, t));
    } finally {
      setBezig("");
    }
  };

  if (!bijlagen.length && !formaten.length) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="antwoord-document">
      {bijlagen.map((att, i) => (
        // CC4-a: een herschrijfvoorstel is geen bestand maar een naleesstap.
        att?.bron === "herschrijfvoorstel"
          ? <HerschrijfVoorstel key={`voorstel-${i}`} voorstel={att} message={message} onBijlagen={setBijlagen} />
          : <BijlageKaart key={`${att.name || "bijlage"}-${i}`} att={att} />
      ))}
      {formaten.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs"
                  onClick={() => setOpen((o) => !o)} aria-expanded={open}
                  data-testid="maak-document">
            <FilePlus2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            {t("Maak document")}
          </Button>
          {open && formaten.map((f) => (
            <Button key={f.formaat} type="button" variant="outline" size="sm" className="h-7 px-2 text-xs"
                    disabled={Boolean(bezig)} onClick={() => maak(f.formaat)}
                    data-testid={`maak-document-${f.formaat}`}>
              {bezig === f.formaat && <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden="true" />}
              {f.naam}
            </Button>
          ))}
        </div>
      )}
      {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
    </div>
  );
}
